import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {merge, Subject} from 'rxjs';
import {debounceTime, filter, takeUntil} from 'rxjs/operators';
import {Sort} from '@angular/material/sort';
import {MatDialog} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatAutocomplete, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {ErrorStateMatcher} from '@angular/material/core';
import {TableControl} from '../../../../../common/models/TableControl';
import {DateUtil} from '../../../../../common/util/DateUtil';
import {SharedService} from '../../../../../common/services/shared-service';
import {PagedResponseDTO} from '../../../../../common/models/PagedResponseDTO';
import {PagedRequestDTO} from '../../../../../common/models/PagedRequestDTO';
import {OtherRequestBuilder} from '../../../../services/other-request-builder';
import {IntegrationErrorMessageDTO} from '../../../../../common/models/IntegrationErrorMessageDTO';
import {IntegrationErrorMessageService} from '../../../../../common/services/integration-error-message.service';
import {
  Common,
  createFieldMap,
  createNewPagedFilterDTO,
  getPaginatorLengthFromPagedResponse,
  getSortDirection
} from '../../../../../common/util/Util';
import {detailsExpand} from '../../../../../common';
import {ContentDisplayDialogComponent} from '../../../dialogs/content-display-dialog/content-display-dialog.component';
import {LookupService} from 'src/app/modules/common/services/lookup.service';
import {LookupValue} from 'src/app/modules/clm/types/lookup-value.enum';
import {NameValueDTO} from 'src/app/modules/common/models/NameValueDTO';
import {PagedFilterDTONew} from '../../../../../common/models/PagedFilterDTO';
import {HelpInfo, HelpInfoInputName} from '../../../../../clm/types/help-info.enum';

enum TableColumns {
  Id = 'id',
  DateTime = 'dateTime',
  MessageId = 'messageId',
  CorrelationId = 'correlationId',
  RouteId = 'routeId',
  IsConsumed = 'isConsumed',
  Time = 'elapsedTime1',
  TransactionType = 'transactionType',
  Message = 'message',
  MessageOutput = 'messageOutput'
}

enum RouteId {
  ClaimRequestAllegra = 'api.claim.request.allegra',
  ClaimRequestGeneral = 'api.claim.request.general',
}

@Component({
  selector: 'app-external-data-view',
  templateUrl: './external-data-view.component.html',
  styleUrls: [
    './external-data-view.component.scss',
    '../../../../../../../styles/global/_management-styles.scss'
  ],
  animations: [detailsExpand]
})
export class ExternalDataViewComponent implements OnChanges, OnInit, OnDestroy {
  readonly allOptions: string = 'All';
  readonly debounceTime: number = Common.DEBOUNCE_TIME;
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly tableColumns = TableColumns;
  readonly InputName = HelpInfoInputName;
  readonly searchByMessageID$: Subject<string> = new Subject<string>();
  readonly searchByCorrelationID$: Subject<string> = new Subject<string>();
  readonly claimRequestRouteIds: string = [
    RouteId.ClaimRequestAllegra,
    RouteId.ClaimRequestGeneral
  ].join(',');

  tableControl: TableControl;
  displayedColumns: string[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<IntegrationErrorMessageDTO> = new MatTableDataSource();
  routeIDTypes: string[] = [this.allOptions];
  consumedStatuses: string[] = [this.allOptions, 'Y', 'N'];
  selectedMessageID: string;
  selectedCorrelationID: string;
  selectedValueForRouteID: string;
  selectedValueForIsConsumed: string;
  searchMessageFormControl: FormControl = new FormControl('', Validators.minLength(3));
  searchMessageByCorrelationIdFormControl: FormControl = new FormControl('', Validators.minLength(3));
  readonly immediateErrorStateMatcher: ErrorStateMatcher = {
    isErrorState: (control: FormControl | null): boolean => !!(control && control.invalid && control.dirty)
  };
  routeIdControl: FormControl = new FormControl(this.allOptions);
  filteredRouteIDTypes: string[] = [this.allOptions];
  private messageCache = new Map<number, IntegrationErrorMessageDTO>();

  @Input() showHeader = true;
  @Input() correlationId: string;
  @ViewChild('errorMessagesPaginator', {static: false}) errorMessagesPaginator: MatPaginator;
  @ViewChild('routeIdAuto') routeIdAuto: MatAutocomplete;

  constructor(
    public dialog: MatDialog,
    private sharedService: SharedService,
    private integrationErrorMessageService: IntegrationErrorMessageService,
    private lookupService: LookupService) {
    const controls = OtherRequestBuilder.getTableControls();
    this.tableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'id',
      'DESCENDING',
      '',
      ''
    );
    this.selectedMessageID = '';
    this.selectedCorrelationID = '';
    this.selectedValueForRouteID = this.routeIDTypes[0];
    this.selectedValueForIsConsumed = this.consumedStatuses[0];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.correlationId && changes.correlationId.currentValue) {
      this.selectedCorrelationID = changes.correlationId.currentValue;
      this.selectedValueForRouteID = this.claimRequestRouteIds;
      this.selectedValueForIsConsumed = 'N';
      this.getListOfIntegrationErrorMessages(this.tableControl);
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.getListOfLookupValues();
    if (!this.correlationId) {
      this.getListOfIntegrationErrorMessages(this.tableControl);
    }
    this.sharedService.getHelpInfo(HelpInfo.ExternalData);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions(): void {

    merge(
      this.searchMessageFormControl.valueChanges.pipe(
        debounceTime(this.debounceTime),
        filter((value: string) => !value || value.length >= 3)
      ),
      this.searchMessageByCorrelationIdFormControl.valueChanges.pipe(
        debounceTime(this.debounceTime),
        filter((value: string) => !value || value.length >= 3)
      )
    )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (_: string) => this.getListOfIntegrationErrorMessages(this.tableControl),
        error: (error) => console.log(error)
      });
  }

  pageChanged($event: PageEvent): void {
    this.tableControl.pageNumber = $event.pageIndex;
    this.tableControl.pageSize = $event.pageSize;
    this.getListOfIntegrationErrorMessages(this.tableControl);
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    this.errorMessagesPaginator?.firstPage();
    this.tableControl.pageNumber = 0;
    this.tableControl.sortField = $event.active;
    this.tableControl.sortDirection = getSortDirection($event.direction);
    this.getListOfIntegrationErrorMessages(this.tableControl);
  }

  getInfoTooltip(inputName: string): string {
    return this.sharedService.getInfoTooltip(inputName);
  }

  onSelectionChange(event: string, filterField: string): void {
    const value: string = event;

    if (filterField === TableColumns.RouteId) {
      this.selectedValueForRouteID = value;
    }
    else if (filterField === TableColumns.IsConsumed) {
      this.selectedValueForIsConsumed = value;
    }
    this.errorMessagesPaginator?.firstPage();
    this.getListOfIntegrationErrorMessages(this.tableControl);
  }

  filterRouteIds(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredRouteIDTypes = value
      ? this.routeIDTypes.filter(option => option.toLowerCase().includes(value))
      : this.routeIDTypes.slice();
  }

  onRouteIdOptionSelected(value: string): void {
    if (value !== this.allOptions && value === this.selectedValueForRouteID) {
      const previousValue = value;
      this.onSelectionChange(this.allOptions, TableColumns.RouteId);
      setTimeout(() => {
        this.routeIdControl.setValue(this.allOptions);
        this.filteredRouteIDTypes = this.routeIDTypes.slice();
        this.routeIdAuto?.options?.find(opt => opt.value === previousValue)?.deselect();
      });
    } else {
      this.onSelectionChange(value, TableColumns.RouteId);
    }
  }

  showAllRouteIds(): void {
    const value = this.routeIdControl.value || '';
    if (!value || value === this.allOptions) {
      this.filteredRouteIDTypes = this.routeIDTypes.slice();
    } else {
      this.filteredRouteIDTypes = this.routeIDTypes.filter(option =>
        option.toLowerCase().includes(value.toLowerCase())
      );
    }
  }

  clearRouteId(input: HTMLInputElement, trigger: MatAutocompleteTrigger): void {
    this.routeIdControl.setValue(this.allOptions);
    this.showAllRouteIds();
    this.onSelectionChange(this.allOptions, TableColumns.RouteId);
    setTimeout(() => {
      input.focus();
      trigger.openPanel();
    });
  }

  onInputChangedSearchMessages($event: Event, filterField: string): void {
    $event.preventDefault();
    $event.stopPropagation();

    const inputValue = ($event.target as HTMLInputElement).value;

    if (inputValue.length > 0 && inputValue.length < 3) {
      if (filterField === TableColumns.MessageId) {
        this.selectedMessageID = '';
      } else if (filterField === TableColumns.CorrelationId) {
        this.selectedCorrelationID = '';
      }
      return;
    }

    this.errorMessagesPaginator?.firstPage();
    this.tableControl.filterValue = inputValue;

    if (filterField === TableColumns.MessageId) {
      this.selectedMessageID = inputValue;
      this.tableControl.filterField = TableColumns.MessageId;
      this.searchByMessageID$.next(this.tableControl.filterValue);
    }
    else if (filterField === TableColumns.CorrelationId) {
      this.selectedCorrelationID = inputValue;
      this.tableControl.filterField = TableColumns.CorrelationId;
      this.searchByCorrelationID$.next(this.tableControl.filterValue);
    }
  }

  openClickOpenMessageDialog($event: MouseEvent, row: IntegrationErrorMessageDTO, field: string): void {
    $event.preventDefault();
    $event.stopPropagation();

    const openDialog = (data: IntegrationErrorMessageDTO) => {
      const content = data[field as keyof IntegrationErrorMessageDTO] as string;
      if (!content) {
        this.sharedService.openSnackBar('No message available');
        return;
      }
      this.dialog.open(ContentDisplayDialogComponent, {
        width: '50vw',
        height: '85vh',
        data: {
          heading: field === TableColumns.Message ? 'Request' : 'Response',
          buttonText: 'Copy',
          message: content
        },
      });
    };

    const cached = this.messageCache.get(row.id);
    if (cached) {
      openDialog(cached);
      return;
    }

    this.integrationErrorMessageService.getMessageById(row.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: IntegrationErrorMessageDTO) => {
          this.messageCache.set(row.id, response);
          openDialog(response);
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error?.businessMessage);
        }
      });
  }

  private getListOfLookupValues(): void {
    this.lookupService.getListOfLookupValues(LookupValue.MessageRouteId)
      .subscribe({
        next: (response: NameValueDTO[]) => {
          this.routeIDTypes = this.routeIDTypes.concat(response.map(x => x.name));
          this.filteredRouteIDTypes = [...this.routeIDTypes];
        },
        error: errorResponse => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      });
  }

  private getListOfIntegrationErrorMessages(tableControl: TableControl): void {
    let pagedRequestDTO: PagedRequestDTO = OtherRequestBuilder.getIntegrationTblMessagesErrorPagedRequestDTO(tableControl);
    pagedRequestDTO = this.getPagedRequestDTO(pagedRequestDTO);

    this.integrationErrorMessageService
      .getListOfIntegrationErrorMessages(pagedRequestDTO)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: PagedResponseDTO<IntegrationErrorMessageDTO>) => {
          this.dataSource.data = response.resultList;
          this.tableControl.length = getPaginatorLengthFromPagedResponse(
            response,
            tableControl.pageNumber,
            tableControl.pageSize
          );
        },
        error: (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error?.businessMessage);
        }
      });
  }

  private getPagedRequestDTO(pagedRequestDTO: PagedRequestDTO): PagedRequestDTO {
    const matchedOperatorBy = '==';
    pagedRequestDTO.fieldMap = {};

    if (this.selectedMessageID) {
      pagedRequestDTO.fieldMap = {
        ...createFieldMap(TableColumns.MessageId, this.selectedMessageID)
      };
    }

    if (this.selectedCorrelationID) {
      pagedRequestDTO.fieldMap = {
        ...pagedRequestDTO.fieldMap,
        [TableColumns.CorrelationId]: createNewPagedFilterDTO(TableColumns.CorrelationId, matchedOperatorBy, this.selectedCorrelationID)
      };
    }

    if (this.selectedValueForRouteID !== this.allOptions) {
      const routeOperator = this.selectedValueForRouteID === this.claimRequestRouteIds ? 'in' : matchedOperatorBy;
      const pagedFilterDTO: PagedFilterDTONew = createNewPagedFilterDTO(TableColumns.RouteId, routeOperator, this.selectedValueForRouteID);
      pagedRequestDTO.fieldMap = {
        ...pagedRequestDTO.fieldMap,
        [TableColumns.RouteId]: pagedFilterDTO
      };
    }

    if (this.selectedValueForIsConsumed !== this.allOptions) {
      const value: string = this.selectedValueForIsConsumed === 'Y' ? '1' : '0';
      const pagedFilterDTO: PagedFilterDTONew = createNewPagedFilterDTO(TableColumns.IsConsumed, matchedOperatorBy, value);
      pagedRequestDTO.fieldMap = {
        ...pagedRequestDTO.fieldMap,
        [TableColumns.IsConsumed]: pagedFilterDTO
      };
    }
    return pagedRequestDTO;
  }

}
